import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Loader2,
  Mail, 
  X, 
} from 'lucide-react';
import { clsx } from 'clsx';
import { useDialogA11y } from '@/hooks/useDialogA11y';

// --- Types ---

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Resolves `true` when the invite succeeded (modal closes). `false` keeps the modal open. */
  onInvite?: (payload: { name: string; email: string; role?: 'admin' | 'user' }) => Promise<boolean> | boolean;
  /** When true, owner may invite as admin. */
  allowAdminInvite?: boolean;
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({ 
  isOpen, 
  onClose,
  onInvite,
  allowAdminInvite = false,
}) => {
  const { t } = useTranslation('common');
  const { t: ts } = useTranslation('settings');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('user');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dialogRootRef = useRef<HTMLDivElement | null>(null);

  // Validation
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValid = isValidEmail && name.trim().length > 0;

  const requestClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  useDialogA11y(dialogRootRef, { isOpen, onClose: requestClose });

  useEffect(() => {
    if (isOpen) {
      setName('');
      setEmail('');
      setRole('user');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!isValid || isSubmitting || !onInvite) return;
    setIsSubmitting(true);
    try {
      const ok = await onInvite({ name: name.trim(), email: email.trim(), role });
      if (ok) {
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="fixed inset-0 z-[160] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity animate-in fade-in duration-300"
        onClick={requestClose}
        aria-hidden
      />

      {/* Modal Container */}
      <div
        ref={dialogRootRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-user-modal-title"
        tabIndex={-1}
        className="relative w-full max-w-lg bg-card modal-surface border border-border shadow-modal overflow-hidden animate-in zoom-in-95 fade-in duration-200 focus:outline-none"
      >

        {/* Header */}
        <div className="flex items-center justify-between px-10 pt-10 pb-2">
          <div className="space-y-1">
            <h2 id="invite-user-modal-title" className="text-2xl font-bold text-foreground">{t('shared.modals.inviteUser.title')}</h2>
            <p className="text-sm text-muted-foreground">{t('shared.modals.inviteUser.description')}</p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={isSubmitting}
            aria-label={t('common.close')}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-full transition-all -mt-8 disabled:pointer-events-none disabled:opacity-40"
          >
            <X size={20} aria-hidden />
          </button>
        </div>

        {/* Content */}
        <div className="px-10 py-8 space-y-6">
          
          {/* Name Field */}
          <div className="space-y-3">
            <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest px-1">{t('shared.modals.inviteUser.fullName')}</label>
            <div className="relative group">
              <input 
                autoFocus
                type="text" 
                placeholder={t('shared.modals.inviteUser.fullNamePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSubmitting}
                className="w-full bg-muted/30 border border-border focus:border-primary/50 rounded-2xl py-3.5 px-5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-4 focus:ring-primary/5 transition-all font-medium disabled:cursor-not-allowed disabled:opacity-60"
              />
            </div>
          </div>

          {/* Email Address Field */}
          <div className="space-y-3">
            <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest px-1">{t('shared.modals.inviteUser.emailAddress')}</label>
            <div className="relative group">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-primary" size={18} />
              <input 
                type="email" 
                placeholder={t('shared.modals.inviteUser.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && isValid && !isSubmitting) {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
                className="w-full bg-muted/30 border border-border focus:border-primary/50 rounded-2xl py-3.5 pl-12 pr-4 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-4 focus:ring-primary/5 transition-all font-medium disabled:cursor-not-allowed disabled:opacity-60"
              />
            </div>
            <p className="text-[11px] text-muted-foreground/60 font-medium px-1">{t('shared.modals.inviteUser.helper')}</p>
          </div>

          {allowAdminInvite ? (
            <div className="space-y-3">
              <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest px-1">
                {ts('team.inviteRoleLabel')}
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as 'admin' | 'user')}
                disabled={isSubmitting}
                className="w-full rounded-2xl border border-border bg-muted/30 px-4 py-3 text-sm font-medium"
              >
                <option value="user">{ts('team.roleUser')}</option>
                <option value="admin">{ts('team.roleAdmin')}</option>
              </select>
            </div>
          ) : null}

        </div>

        {/* Footer */}
        <div className="px-10 py-8 bg-muted/20 border-t border-border-muted flex items-center justify-end gap-5">
          <button 
            type="button"
            onClick={requestClose}
            disabled={isSubmitting}
            className="px-6 py-2.5 text-sm font-bold text-muted-foreground hover:text-foreground transition-colors disabled:pointer-events-none disabled:opacity-40"
          >
            {t('common.cancel')}
          </button>
          <button 
            type="button"
            onClick={() => void handleSend()}
            disabled={!isValid || isSubmitting}
            className={clsx(
              "inline-flex min-w-[9.5rem] items-center justify-center gap-2 px-8 py-2.5 rounded-lg text-sm font-bold transition-all active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              isValid && !isSubmitting
                ? "bg-primary hover:bg-primary/90 text-primary-foreground" 
                : "bg-muted text-muted-foreground cursor-not-allowed border border-border-muted"
            )}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={16} className="animate-spin" aria-hidden />
                {t('shared.modals.inviteUser.sending')}
              </>
            ) : (
              t('shared.modals.inviteUser.sendInvitation')
            )}
          </button>
        </div>

      </div>
    </div>
  );
};
